import {
    DotLegalAlert,
    DotLegalCheckbox,
    DotLegalFullScreenSpinner,
    DotLegalLink,
    DotLegalMultiSelect,
    DotLegalSelect,
} from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { isTemplateCustomer } from "../../common/templateCustomerHelper";
import { useTranslation } from "../../localization/useTranslation";
import { useAddSystemPane } from "./AddSystemPane.hooks";
import TrialUsageWarning from "../../common/components/TrialUsageWarning.tsx";
import { Alert, AlertTitle, Box } from "@mui/material";
import { Trans } from "react-i18next";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { definePane } from "../../main/panes/definePane";
import TemplateStepFields from "../../entityTemplates/templateStepFields/TemplateStepFields.tsx";
import { AddSystemPaneProps } from "./AddSystemPane.types";
import SystemTemplateField from "./SystemTemplateField.tsx";

function AddSystemPane(props: AddSystemPaneProps) {
    const {
        createSystem,
        inProgress,
        validation,
        systemModel,
        onModelChange,
        templateTypes,
        templates,
        templatesIsLoading,
        isLoadingTemplate,
        showInfoSecUpgradeAlert,
        onDismissInfoSecAlert,
        selectedTemplateMissingAccess,
        fieldGroups,
        selectedTagIds,
        onTagsChange,
        creationFieldSelections,
        onCreationFieldChange,
        requiredFieldValidationErrors,
        editedSystem,
        onTemplateFieldChange,
        onTemplateChange,
        permissions,
    } = useAddSystemPane(props);
    const { translateString } = useTranslation();
    const { language } = useUserContext();

    validation.check(systemModel);

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={translateString("createSystem")}
            okButtonText={translateString("create")}
            onOkClick={createSystem}
            inProgress={inProgress}
            okButtonDisabled={selectedTemplateMissingAccess}
        >
            <DotLegalSelect
                options={templates}
                onChange={onTemplateChange}
                label={translateString("assetTemplate")}
                noOptionsLabel={translateString("noOptions")}
                isLoading={templatesIsLoading}
                toolTipText={translateString("addSystemTemplateTooltip")}
                disableClearable
                selectedItem={systemModel.entityTemplateId}
                errorText={validation?.getErrorsForDisplay("entityTemplateId")}
            />

            {selectedTemplateMissingAccess && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                </Box>
            )}

            {isLoadingTemplate && <DotLegalFullScreenSpinner />}

            {!selectedTemplateMissingAccess && (
                <TemplateStepFields
                    groups={fieldGroups}
                    renderField={(field) => (
                        <SystemTemplateField
                            field={field}
                            name={systemModel.name}
                            onNameChange={(name) => onModelChange({ ...systemModel, name })}
                            nameErrorText={validation?.getErrorsForDisplay("name")}
                            system={editedSystem}
                            onChange={onTemplateFieldChange}
                            validationErrors={requiredFieldValidationErrors}
                            selectedTagIds={selectedTagIds}
                            onTagsChange={onTagsChange}
                            creationFieldSelections={creationFieldSelections}
                            onCreationFieldChange={onCreationFieldChange}
                        />
                    )}
                />
            )}

            {showInfoSecUpgradeAlert && (
                <Alert sx={{ marginTop: 2 }} severity="info" onClose={onDismissInfoSecAlert}>
                    <AlertTitle sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}>{translateString("upgradeToInformationSecurity")}</AlertTitle>
                    <Box component={"span"} sx={(theme) => ({ fontSize: theme.typography.pxToRem(12) })}>
                        <Trans
                            i18nKey={"upgradeToInformationSecurityInfo"}
                            components={{
                                plan: (
                                    <DotLegalLink
                                        bold
                                        to={
                                            language === "da"
                                                ? "https://www.dotlegal.com/da/produkt/informations-sikkerheds-software"
                                                : "https://www.dotlegal.com/en/product/information-security-management-software"
                                        }
                                        linkColor="primary"
                                    >
                                        {undefined}
                                    </DotLegalLink>
                                ),
                            }}
                        />
                    </Box>
                </Alert>
            )}

            {permissions.canManageCustomers && isTemplateCustomer() && (
                <React.Fragment>
                    <DotLegalMultiSelect
                        options={templateTypes}
                        chosenOptions={systemModel.systemTypes.map((x) => {
                            return x.toString();
                        })}
                        placeholder={"Type"}
                        label={"Type"}
                        onChange={(systemTypes) => onModelChange({ ...systemModel, systemTypes })}
                        noOptionsLabel={translateString("noOptions")}
                    />

                    <DotLegalCheckbox
                        label={"Mest populær?"}
                        checked={systemModel.mostPopular}
                        onChange={(mostPopular) => onModelChange({ ...systemModel, mostPopular })}
                    />
                </React.Fragment>
            )}

            {props.shouldDisplayCreateSystemWarning && <TrialUsageWarning textKey={"createExceedingNumberOfSystemWarning"} marginTop={3} />}
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: AddSystemPane });
